import React, { useMemo } from 'react';
import { View, Image, StyleSheet, ImageSourcePropType } from 'react-native';
import { Text } from '@/components/common/text';
import { useMarketplaceColors } from '@/constants/residents/marketplaceColors';

export type AvatarSize = 'small' | 'medium' | 'large' | 'xlarge' | 'xxlarge';

interface UserAvatarProps {
  initials: string;
  size?: AvatarSize;
  /** Optional avatar image URL or source */
  avatarUrl?: string;
  imageSource?: ImageSourcePropType;
}

/**
 * Avatar size configurations:
 * - small: 26x26 (chat messages)
 * - medium: 40x40 (chat header thumbnails)
 * - large: 46x46 (cards, inbox, feedback)
 * - xlarge: 68x68 (profile screens mobile)
 * - xxlarge: 80x80 (profile screens desktop)
 */
const SIZES: Record<AvatarSize, { container: number; font: number }> = {
  small: { container: 26, font: 12 },
  medium: { container: 40, font: 14 },
  large: { container: 46, font: 14 },
  xlarge: { container: 68, font: 24 },
  xxlarge: { container: 80, font: 28 },
};

/**
 * Reusable circular avatar with user initials or image
 * Used consistently across marketplace for user avatars
 */
export const UserAvatar: React.FC<UserAvatarProps> = ({ 
  initials, 
  size = 'small',
  avatarUrl,
  imageSource,
}) => {
  const mc = useMarketplaceColors();

  const styles = useMemo(() => StyleSheet.create({
    container: {
      backgroundColor: mc.white10Percent,
      alignItems: 'center',
      justifyContent: 'center',
    },
    text: {
      fontWeight: '600',
      color: mc.textPrimary,
      textTransform: 'uppercase',
    },
    image: {
      backgroundColor: mc.white10Percent,
    },
  }), [mc]);

  const dimensions = SIZES[size];
  const containerStyle = {
    width: dimensions.container,
    height: dimensions.container,
    borderRadius: dimensions.container / 2,
  };

  // Render image avatar if URL or source is provided
  if (avatarUrl || imageSource) {
    return (
      <Image
        source={imageSource || { uri: avatarUrl }}
        style={[styles.image, containerStyle]}
      />
    );
  }
  
  // Render initials avatar
  return (
    <View style={[styles.container, containerStyle]}>
      <Text scale="micro" style={[styles.text, { fontSize: dimensions.font }]}>
        {initials}
      </Text>
    </View>
  );
};
